import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sl';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { EpisodesApi } from '../../../network';
import { EpisodeAdminModel } from '../../../network/rest';
import ApiErrorHandler from '../../../utils/ApiErrorHandler';
import { translations } from '../../../utils/staticContent';
import ImageUploadForm from '../../components/ImageUploadForm';
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor';

export default function ShowsEpisodesForm({ episode }: { episode?: EpisodeAdminModel }) {
    const [title, setTitle] = useState(episode?.title || '');
    const [html, setHtml] = useState('');
    const [episodeNumber, setEpisodeNumber] = useState(episode?.episodeNumber || 0);
    const [content, setContent] = useState(episode?.content || '');
    const [shortContent, setShortContent] = useState(episode?.shortContent || '');
    const [originalDate, setOriginalDate] = useState<Dayjs | null>(
        episode?.originalDate ? dayjs(episode.originalDate) : null,
    );
    const [fileId, setFileId] = useState('');
    const [dateError, setDateError] = useState(false);
    const [error, setError] = useState('');
    const { showId } = useParams();

    useEffect(() => {
        if (episode) {
            getData(episode.id);
        }
    }, []);

    async function getData(id: string) {
        try {
            const response = await EpisodesApi.getEpisodeAdminApiV1EpisodesAdminEpisodeGet({
                id,
            });
            if (response) {
                setTitle(response.title);
                setHtml('');
                setEpisodeNumber(response.episodeNumber);
                setOriginalDate(dayjs(response.originalDate));
                setContent(response.content);
                setShortContent(response.shortContent);
            }
        } catch (e) {
            ApiErrorHandler(e);
        }
    }
    return (
        <div className="ShowsEpisodesForm">
            <Container component="main" maxWidth="md">
                <Grid
                    container
                    component="form"
                    rowSpacing={2}
                    columnSpacing={{ xs: 1, sm: 2 }}
                    noValidate
                    mt={1}
                    onSubmit={async (e) => {
                        e.preventDefault();
                        if (!originalDate) {
                            return;
                        }
                        try {
                            const formattedDate = new Date(originalDate.toDate());

                            if (episode) {
                                await EpisodesApi.updateEpisodeApiV1EpisodesEpisodePut({
                                    id: episode.id,
                                    bodyUpdateEpisodeApiV1EpisodesEpisodePut: {
                                        title,
                                        episodeNumber,
                                        originalDate: formattedDate,
                                        content,
                                        shortContent,
                                    },
                                });
                            } else {
                                if (!showId) {
                                    return;
                                }
                                await EpisodesApi.createEpisodeApiV1EpisodesEpisodePost({
                                    episodeCreateModel: {
                                        showId,
                                        title,
                                        episodeNumber,
                                        originalDate: formattedDate,
                                        html,
                                        content,
                                        shortContent,
                                        fileId,
                                    },
                                });
                                setTitle('');
                                setHtml('');
                                setEpisodeNumber(0);
                                setOriginalDate(dayjs(''));
                                setContent('');
                                setShortContent('');
                            }
                        } catch (e) {
                            const error = ApiErrorHandler(e);
                            setError(error);
                        }
                    }}
                >
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            id="title"
                            label={translations.title}
                            name="title"
                            autoFocus
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            id="episodeNumber"
                            label={translations.episodeNumber}
                            name="episodeNumber"
                            type="number"
                            autoFocus
                            value={episodeNumber}
                            onChange={(e) => setEpisodeNumber(+e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sl">
                            <DateTimePicker
                                label={translations.dateRequired}
                                onChange={(newValue) => {
                                    setOriginalDate(newValue);
                                }}
                                format="DD/MM/YYYY hh:mm"
                                slotProps={{ textField: { fullWidth: true } }}
                                onError={(e) => (e ? setDateError(true) : setDateError(false))}
                                value={originalDate}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <RichTextEditor
                            value={shortContent}
                            onChange={setShortContent}
                            label={translations.shortContent}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <RichTextEditor
                            value={content}
                            onChange={setContent}
                            label={translations.content}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <TextField
                            required
                            fullWidth
                            name="html"
                            label="html"
                            id="html"
                            value={html}
                            onChange={(e) => setHtml(e.target.value)}
                            multiline={true}
                        />
                    </Grid>
                    <ImageUploadForm setError={setError} fileId={fileId} setFileId={setFileId} />
                    <Grid item xs={12} sm={12}>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                            disabled={
                                title.length < 5 ||
                                title.length > 50 ||
                                episodeNumber < 1 ||
                                !html.length ||
                                !originalDate ||
                                dateError
                            }
                        >
                            {translations.save}
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography component="span" color="red">
                            {error}
                        </Typography>
                    </Grid>
                </Grid>
            </Container>
        </div>
    );
}
